<template>
    <Modal
      ref="feedbackHistoryModalRef"
      :showIcon="false"
      :clickToClose="true"
      class="feedback-history-modal"
      :modalStyle="{ width: '1100px', height: 'auto', maxHeight: '90vh' }"
    >
      <template v-slot:body>
        <template v-if="!isProductDetailLoading">
          <div class="header">
            <p class="title">Güncelleme/Hata Talebi Geçmişi</p>
          </div>
          
          <div class="table-container">
            <div v-if="isLoading" class="loading">
              <img src="@/assets/images/loading.gif" alt="Loading" />
            </div>
            
            <template v-else-if="feedbacks.length > 0">
              <div class="table-wrapper table-scroll">
                <table class="feedback-table">
                  <thead>
                    <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                      <th
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :colSpan="header.colSpan"
                        :style="{ width: `${header.getSize()}px` }"
                      >
                        <FlexRender 
                          v-if="!header.isPlaceholder" 
                          :render="header.column.columnDef.header"
                          :props="header.getContext()" 
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in table.getRowModel().rows" :key="row.id">
                      <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                        <FlexRender 
                          :render="cell.column.columnDef.cell" 
                          :props="cell.getContext()" 
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div class="pagination-wrapper">
                <Pagination
                  v-if="pageCount > 1"
                  :pageCount="pageCount"
                  :pageRange="5"
                  :clickHandler="handlePageChange"
                  :firstLastButton="true"
                  containerClass="button-group"
                  prevLinkClass="border left rounded p-1"
                  pageLinkClass="border rounded p-1"
                  nextLinkClass="border right rounded p-1"
                  :marginPages="0"
                  :value="pageIndex"
                />
    
                <div class="per-page-wrapper">
                  <span class="per-page-text">Her Sayfada</span>
                  <SimpleSelect
                    :simpleSelectItems="pageSizes"
                    :selectedKey="pageSize"
                    openMenuPosition="top-left"
                    @btnItemClick="handlePageSizeChange($event)"
                  />
                </div>
              </div>
            </template>
          </div>
        </template>
        <template v-else>
          <ProductHistoryModalSkeleton />
        </template>
      </template>
    </Modal>
  </template>
  
  <script setup>
  import { ref, h } from 'vue';
  import { useStore } from 'vuex';
  import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
  import { Modal, SimpleSelect, TooltipPlugin, Icons } from '@n11/so-ui-components';
  import Pagination from '@/components/Table/Pagination';
  import ProductHistoryModalSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductHistoryModalSkeleton.vue'

  const props = defineProps({
    isProductDetailLoading: {
      type: Boolean,
      default: false,
    },
  });
  const INITIAL_PAGE_SIZE = 10;
  
  const pageSizes = {
    10: '10 Satır',
    25: '25 Satır',
    50: '50 Satır',
    100: '100 Satır',
  };
  
  const feedbackStatusConfig = {
    Open: { label: 'İncelemede', class: 'pending', icon: 'SuspendedIcon', color: '#4FA4E5' },
    InReview: { label: 'İncelemede', class: 'pending', icon: 'SuspendedIcon', color: '#4FA4E5' },
    Rejected: { label: 'Reddedildi', class: 'rejected', icon: 'ProhibitedIcon', color: '#D13333' },
    Resolved: { label: 'Çözüldü', class: 'resolved', icon: 'CheckCircle', color: '#5EBA57' },
    Closed: { label: 'Çözüldü', class: 'resolved', icon: 'CheckCircle', color: '#5EBA57' },
  };
  
  const feedbackTypeLabels = {
    IMAGE: 'Ürün Görseli',
    ATTRIBUTE_VALUE: 'Ürün Özelliği',
    TITLE: 'Ürün Adı',
    USC: 'Barkod / GTIN',
    DESCRIPTION: 'Ürün Açıklaması',
  };
  
  const defaultStatusConfig = { label: '', class: 'pending', icon: 'Clock', color: '#4FA4E5' };
  
  const store = useStore();
  const feedbackHistoryModalRef = ref(null);
  const feedbacks = ref([]);
  const isLoading = ref(false);
  const pageIndex = ref(0);
  const pageSize = ref(INITIAL_PAGE_SIZE);
  const pageCount = ref(0);
  
  const getStatusConfig = (status) => feedbackStatusConfig[status] || defaultStatusConfig;
  const getTypeLabel = (type) => feedbackTypeLabels[type] || type;
  
  const columns = [
    {
      accessorKey: 'createdDate',
      header: 'Talep Oluşturma Zamanı',
      size: 140,
      cell: (info) => h('span', { class: 'cell-text' }, info.getValue() || '-')
    },
    {
      accessorKey: 'id',
      header: 'Güncelleme ID',
      size: 140,
      cell: (info) => h('span', { class: 'cell-text' }, info.getValue() || '-')
    },
    {
      accessorKey: 'type',
      header: 'Talep Türü',
      size: 140,
      cell: (info) => h('span', { class: 'cell-text' }, getTypeLabel(info.getValue()) || '-')
    },
    {
      accessorKey: 'description',
      header: 'Talep Açıklaması',
      size: 170,
      cell: (info) => {
        const value = info.getValue();
        const displayValue = value || '-';
        const isLong = value && value.length > 20;
        
        const truncateStyle = {
          display: 'block',
          maxWidth: '150px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: 'default',
          fontSize: '13px',
          lineHeight: '18px',
        };
        
        if (isLong) {
          return h(TooltipPlugin, { tooltipText: value, tooltipWidth: 300 }, {
            'tooltip-btn': () => h('span', { style: truncateStyle }, displayValue)
          });
        }
        return h('span', { class: 'cell-text' }, displayValue);
      }
    },
    {
      accessorKey: 'status',
      header: 'Talep Durumu',
      size: 140,
      cell: (info) => {
        const status = info.getValue();
        const config = getStatusConfig(status);
        return h('span', { class: ['status-badge', config.class] }, [
          h(Icons, { name: config.icon, color: config.color, size: 'xs' }),
          config.label || status
        ]);
      }
    },
    {
      accessorKey: 'evaluation',
      header: 'Talep Yanıtı',
      size: 170,
      cell: (info) => {
        const value = info.getValue();
        const displayValue = value || '-';
        const isLong = value && value.length > 20;
        
        const truncateStyle = {
          display: 'block',
          maxWidth: '150px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: 'default',
          fontSize: '13px',
          lineHeight: '18px',
        };
        
        if (isLong) {
          return h(TooltipPlugin, { tooltipText: value, tooltipWidth: 300 }, {
            'tooltip-btn': () => h('span', { style: truncateStyle }, displayValue)
          });
        }
        return h('span', { class: 'cell-text' }, displayValue);
      }
    },
    {
      accessorKey: 'updatedDate',
      header: 'Güncelleme Zamanı',
      size: 140,
      cell: (info) => h('span', { class: 'cell-text' }, info.getValue() || '-')
    }
  ];
  
  const table = useVueTable({
    get data() {
      return feedbacks.value;
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  
  const fetchData = async () => {
    isLoading.value = true;
    
    try {
      const response = await store.dispatch('products/getProductUpdateHistory', {
        page: pageIndex.value,
        size: pageSize.value
      });
      feedbacks.value = response?.feedbacks || [];
      pageCount.value = response?.totalPages || 0;
    } catch (err) {
      console.error('Talep geçmişi yüklenirken hata oluştu:', err);
    } finally {
      isLoading.value = false;
    }
  };
  
  const handlePageChange = async (page) => {
    if (page < 0 || page >= pageCount.value) return;
    pageIndex.value = page;
    await fetchData();
  };
  
  const handlePageSizeChange = async (size) => {
    pageSize.value = Number(size);
    pageIndex.value = 0;
    await fetchData();
  };
  
  const openModal = async () => {
    pageIndex.value = 0;
    pageCount.value = 0;
    feedbacks.value = [];
    feedbackHistoryModalRef.value?.openModal();
    await fetchData();
  };
  
  defineExpose({
    openModal,
  });
  </script>
  
  <style scoped lang="scss">
  @import "@/assets/styles/variables.scss";
  
  .header {
    .title {    
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
      color: $colorN80;
      text-align: left;
      margin-bottom: 12px;
    }
  }
  
  .table-container {
    display: flex;
    gap: 16px;
    flex-direction: column;
  
    .loading {
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
  
      img {
        width: 50px;
        height: 50px;
      }
    }
  
    .empty-state {
      display: flex;
      padding: 64px 0;
      align-items: center;
      flex-direction: column;
      border-radius: 8px;
      border: 1px solid $colorN50;
  
      .title {
        font-size: 16px;
        font-weight: 700;
        color: $colorN80;
      }
    }
  }
  
  .table-wrapper {
    border: 1px solid $colorN50;
    border-radius: 8px;
    max-height: 450px;
    overflow-y: auto;
    scrollbar-width: thin;
    -ms-overflow-style: none; 
  
    &::-webkit-scrollbar {
      display: none;
    }
  }
  
  .feedback-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  
    thead {
      th {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        color: $colorN80;
        background-color: $colorBe30;
        border-bottom: 2px solid $colorN30;
  
        &:first-child {
          border-top-left-radius: 8px;
        }
  
        &:last-child {
          border-top-right-radius: 8px;
        }
  
        &:not(:last-child) {
          border-right: 0.5px solid $colorN50;
        }
      }
    }
  
    tbody {
      td {
        padding: 16px 12px;
        text-align: left;
  
        &:not(:last-child) {
          border-right: 0.5px solid $colorN50;
        }
  
        .cell-text {
          font-size: 13px;
          line-height: 18px;
          letter-spacing: -0.05px;
          color: $colorN80;
        }
  
        .truncate {
          display: block;
          max-width: 150px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: default;
        }
      }
  
      tr:not(:last-child) td {
        border-bottom: 1px solid $colorN50;
      }
    }
  }
  
  .status-badge {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid $colorN30;
    width: fit-content;
  
    &.rejected {
      background-color: $colorE05;
      color: $colorE60;
    }
  
    &.resolved {
      background-color: $colorS05;
      color: $colorS50;
    }
  
    &.pending {
      background-color: $colorB05;
      color: $colorB50;
    }
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    padding-top: 16px;
  
    .per-page-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
  
      .per-page-text {
        font-size: 12px;
        margin-left: 15px;
        width: 70px;
        color: $colorN80;
      }
    }
  }
  </style>
  